import { LucideIcon } from 'lucide-react';

interface BookingDetailRowProps {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  /** Optional inline action link under the subtitle (e.g. "Cancel booking"). */
  actionText?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  /** Muted note shown in place of the action when it's disabled (e.g. "Cancellation period closed"). */
  note?: string;
  testId?: string;
}

/** Icon + title + subtitle (+ optional action) row — the resource-booking `IconBlock` equivalent. */
export const BookingDetailRow = ({
  icon: Icon,
  title,
  subtitle,
  actionText,
  onAction,
  actionDisabled,
  note,
  testId,
}: BookingDetailRowProps): JSX.Element => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <Icon size={20} strokeWidth={1.75} className="mt-0.5 shrink-0 text-system-grey-dark" aria-hidden />
    <div className="min-w-0 flex-1">
      <div className="text-[15px] font-semibold text-foreground">{title}</div>
      {subtitle && <div className="mt-0.5 text-sm text-system-grey-dark">{subtitle}</div>}
      {actionText && onAction && (
        <button
          type="button"
          onClick={onAction}
          disabled={actionDisabled}
          className="mt-1.5 block cursor-pointer rounded text-sm font-semibold text-system-pink outline-none focus-visible:ring-2 focus-visible:ring-system-blue focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:text-system-grey-dark-alt"
        >
          {actionText}
        </button>
      )}
      {/*
        `grey-dark`, not `grey-medium`: the latter is #eaecee, a SURFACE token — it also backs
        `--border` and `--accent` — so as text on white it sits at ~1.2:1 and is effectively
        invisible. This is the same size and weight as the subtitle above it, so it takes the same
        text grey; the line it sits on is what makes it secondary, not a lighter colour.
      */}
      {note && <div className="mt-1.5 text-sm text-system-grey-dark">{note}</div>}
    </div>
  </div>
);
